import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    Zap, Calendar, Clock, MapPin, Check, X, RefreshCw,
    Loader2, ChevronRight, Trophy, TrendingUp, Users,
    AlertCircle, BarChart3, Settings, Eye, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { ScrollArea } from '../components/ui/scroll-area';
import { Separator } from '../components/ui/separator';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    Alert,
    AlertDescription,
} from '../components/ui/alert';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { format } from 'date-fns';
import axios from 'axios';
import { getToken } from '../lib/auth';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Market type labels
const MARKET_LABELS = {
    'odds_evens': { name: 'Odd/Even', icon: '🎲', description: 'Will odd or even numbered runner win' },
    'red_black': { name: 'Red/Black', icon: '🎯', description: 'Top half vs bottom half of field' },
    'fav_vs_field': { name: 'Favourite vs Field', icon: '⭐', description: 'Will the favourite beat everyone' },
    'winning_distance': { name: 'Winning Distance', icon: '📏', description: 'Close finish or comfortable win' },
    'fav_to_place': { name: 'Favourite to Place', icon: '🏆', description: 'Will favourite finish in places' },
    'all_finish': { name: 'All Finish', icon: '🏇', description: 'Will all runners complete the race (Jump)' },
    'high_low_draw': { name: 'High/Low Draw', icon: '📊', description: 'High or low stall number wins (Flat)' },
};

export default function AdminPredictionsPage() {
    const { user, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const token = getToken();
    
    const [activeTab, setActiveTab] = useState('races');
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [selectedRegions, setSelectedRegions] = useState(['UK', 'IRE']);
    const [races, setRaces] = useState([]);
    const [loadingRaces, setLoadingRaces] = useState(false);
    const [enabledMarkets, setEnabledMarkets] = useState([]);
    const [loadingEnabled, setLoadingEnabled] = useState(false);
    const [enablingRace, setEnablingRace] = useState(null);
    const [selectedMarketDetail, setSelectedMarketDetail] = useState(null);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [settlingRace, setSettlingRace] = useState(null);

    useEffect(() => {
        if (!isAuthenticated || !user?.is_admin) {
            toast.error('Admin access required');
            navigate('/');
            return;
        }
    }, [isAuthenticated, user, navigate]);

    // Fetch races for selected date
    const fetchRaces = useCallback(async () => {
        setLoadingRaces(true);
        try {
            const dateStr = format(selectedDate, 'dd-MM-yyyy');
            const countriesParam = selectedRegions.join(',');
            const res = await axios.get(`${API_URL}/api/admin/predictions/races?date=${dateStr}&countries=${countriesParam}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setRaces(res.data.races || []);
        } catch (err) {
            console.error('Failed to fetch races:', err);
            toast.error('Failed to load races');
        } finally {
            setLoadingRaces(false);
        }
    }, [selectedDate, selectedRegions, token]);

    // Fetch enabled markets
    const fetchEnabledMarkets = useCallback(async () => {
        setLoadingEnabled(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/predictions/enabled`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setEnabledMarkets(res.data.markets || []);
        } catch (err) {
            console.error('Failed to fetch enabled markets:', err);
        } finally {
            setLoadingEnabled(false);
        }
    }, [token]);

    useEffect(() => {
        if (activeTab === 'races') {
            fetchRaces();
        } else if (activeTab === 'enabled') {
            fetchEnabledMarkets();
        }
    }, [activeTab, fetchRaces, fetchEnabledMarkets]);

    // Enable predictions for a race
    const enablePredictions = async (race) => {
        setEnablingRace(race.race_id);
        try {
            const res = await axios.post(`${API_URL}/api/admin/predictions/enable`, {
                race_id: race.race_id,
                race_name: race.race_name,
                meeting_name: race.meeting_name,
                race_time: race.race_time,
                is_jump_race: race.is_jump_race
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(res.data.message);
            fetchRaces();
            fetchEnabledMarkets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to enable predictions');
        } finally {
            setEnablingRace(null);
        }
    };

    // Disable predictions for a race
    const disablePredictions = async (raceId) => {
        try {
            await axios.delete(`${API_URL}/api/admin/predictions/disable/${raceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success('Predictions disabled');
            fetchRaces();
            fetchEnabledMarkets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to disable predictions');
        }
    };

    // Purge stale markets (races finished 24h+ ago) — also runs hourly on its own
    const [cleaningUp, setCleaningUp] = useState(false);
    const cleanupStaleMarkets = async () => {
        setCleaningUp(true);
        try {
            const res = await axios.post(`${API_URL}/api/admin/predictions/cleanup-stale`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success(res.data.message);
            fetchRaces();
            fetchEnabledMarkets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to clean up stale markets');
        } finally {
            setCleaningUp(false);
        }
    };

    // View market detail
    const viewMarketDetail = async (raceId) => {
        setLoadingDetail(true);
        try {
            const res = await axios.get(`${API_URL}/api/admin/predictions/market/${raceId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedMarketDetail(res.data);
        } catch (err) {
            toast.error('Failed to load market details');
        } finally {
            setLoadingDetail(false);
        }
    };

    // Settle predictions for a race
    const settlePredictions = async (raceId) => {
        setSettlingRace(raceId);
        try {
            const res = await axios.post(`${API_URL}/api/admin/predictions/settle/${raceId}`, {}, {
                headers: { Authorization: `Bearer ${token}` }
            });
            
            toast.success(`Settled ${res.data.bets_settled} predictions. Winner: ${res.data.winner}`);
            
            // Update market status
            await axios.patch(`${API_URL}/api/admin/predictions/market/${raceId}/status`, {
                status: 'settled'
            }, {
                headers: { Authorization: `Bearer ${token}` }
            }).catch(() => {});
            
            fetchEnabledMarkets();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to settle predictions');
        } finally {
            setSettlingRace(null);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <div className="border-b bg-card">
                <div className="container-app py-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <h1 className="text-2xl font-heading font-bold flex items-center gap-2 page-title">
                                <Zap className="h-6 w-6 text-primary" />
                                Predictions Management
                            </h1>
                            <p className="text-muted-foreground mt-1">
                                Enable and manage prediction markets for races
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Main Content */}
            <div className="container-app py-6">
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                    <TabsList className="mb-6">
                        <TabsTrigger value="races" className="gap-2">
                            <Calendar className="h-4 w-4" />
                            Races
                        </TabsTrigger>
                        <TabsTrigger value="enabled" className="gap-2">
                            <Check className="h-4 w-4" />
                            Enabled Markets
                        </TabsTrigger>
                    </TabsList>

                    {/* Races Tab */}
                    <TabsContent value="races">
                        <div className="grid lg:grid-cols-4 gap-6">
                            {/* Calendar */}
                            <Card className="lg:col-span-1">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-lg">Select Date</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <CalendarComponent
                                        mode="single"
                                        selected={selectedDate}
                                        onSelect={(date) => date && setSelectedDate(date)}
                                        className="rounded-md border"
                                    />
                                    {/* Region selector */}
                                    <div className="mt-4">
                                        <p className="text-xs text-muted-foreground mb-2 font-medium">Regions</p>
                                        <div className="flex flex-wrap gap-1.5">
                                            {[
                                                { code: 'UK', label: '🇬🇧 UK' },
                                                { code: 'IRE', label: '🇮🇪 IRE' },
                                                { code: 'USA', label: '🇺🇸 USA' },
                                                { code: 'FR', label: '🇫🇷 FR' },
                                                { code: 'AUS', label: '🇦🇺 AUS' },
                                                { code: 'ZA', label: '🇿🇦 ZA' },
                                            ].map(({ code, label }) => {
                                                const isSelected = selectedRegions.includes(code);
                                                return (
                                                    <button
                                                        key={code}
                                                        onClick={() => {
                                                            setSelectedRegions(prev =>
                                                                isSelected
                                                                    ? prev.filter(r => r !== code).length > 0
                                                                        ? prev.filter(r => r !== code)
                                                                        : prev  // prevent deselecting all
                                                                    : [...prev, code]
                                                            );
                                                        }}
                                                        className={`px-2 py-1 rounded text-xs font-medium border transition-colors ${
                                                            isSelected
                                                                ? 'bg-primary text-primary-foreground border-primary'
                                                                : 'bg-background text-muted-foreground border-border hover:border-primary/50'
                                                        }`}
                                                    >
                                                        {label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    <Button
                                        variant="outline"
                                        className="w-full mt-4"
                                        onClick={fetchRaces}
                                    >
                                        <RefreshCw className={`h-4 w-4 mr-2 ${loadingRaces ? 'animate-spin' : ''}`} />
                                        Refresh
                                    </Button>
                                </CardContent>
                            </Card>

                            {/* Races List */}
                            <div className="lg:col-span-3">
                                <Card>
                                    <CardHeader>
                                        <CardTitle className="flex items-center justify-between">
                                            <span>Races - {format(selectedDate, 'EEEE, MMMM d, yyyy')}</span>
                                            <Badge variant="outline">
                                                {races.filter(r => r.predictions_enabled).length} / {races.length} enabled
                                            </Badge>
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                        {loadingRaces ? (
                                            <div className="py-12 text-center">
                                                <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                                            </div>
                                        ) : races.length === 0 ? (
                                            <div className="py-12 text-center text-muted-foreground">
                                                <Calendar className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                                <p>No races found for this date</p>
                                            </div>
                                        ) : (
                                            <ScrollArea className="h-[500px]">
                                                <div className="space-y-3">
                                                    {races.map((race) => (
                                                        <div 
                                                            key={race.race_id}
                                                            className={`p-4 rounded-lg border ${
                                                                race.predictions_enabled 
                                                                    ? 'bg-primary/5 border-primary/30' 
                                                                    : 'bg-muted/50'
                                                            }`}
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2">
                                                                        <span className="font-medium">{race.race_name}</span>
                                                                        {race.is_jump_race && (
                                                                            <Badge variant="secondary" className="text-xs">Jump</Badge>
                                                                        )}
                                                                        {race.predictions_enabled && (
                                                                            <Badge variant="default" className="text-xs">
                                                                                <Check className="h-3 w-3 mr-1" />
                                                                                Enabled
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                    <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                                                        <span className="flex items-center gap-1">
                                                                            <MapPin className="h-3 w-3" />
                                                                            {race.meeting_name}
                                                                        </span>
                                                                        <span className="flex items-center gap-1">
                                                                            <Clock className="h-3 w-3" />
                                                                            {race.race_time ? new Date(race.race_time).toLocaleTimeString('en-GB', {
                                                                                hour: '2-digit',
                                                                                minute: '2-digit'
                                                                            }) : 'TBC'}
                                                                        </span>
                                                                        <span>{race.race_type}</span>
                                                                    </div>
                                                                </div>
                                                                <div className="flex items-center gap-2">
                                                                    {race.predictions_enabled ? (
                                                                        <>
                                                                            <Button
                                                                                variant="outline"
                                                                                size="sm"
                                                                                onClick={() => viewMarketDetail(race.race_id)}
                                                                            >
                                                                                <Eye className="h-4 w-4 mr-1" />
                                                                                View
                                                                            </Button>
                                                                            <Button
                                                                                variant="destructive"
                                                                                size="sm"
                                                                                onClick={() => disablePredictions(race.race_id)}
                                                                            >
                                                                                <X className="h-4 w-4" />
                                                                            </Button>
                                                                        </>
                                                                    ) : (
                                                                        <Button
                                                                            onClick={() => enablePredictions(race)}
                                                                            disabled={enablingRace === race.race_id}
                                                                            size="sm"
                                                                        >
                                                                            {enablingRace === race.race_id ? (
                                                                                <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                                            ) : (
                                                                                <Zap className="h-4 w-4 mr-1" />
                                                                            )}
                                                                            Enable Predictions
                                                                        </Button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </ScrollArea>
                                        )}
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    </TabsContent>

                    {/* Enabled Markets Tab */}
                    <TabsContent value="enabled">
                        <Card>
                            <CardHeader>
                                <CardTitle className="flex items-center justify-between">
                                    <span>Enabled Prediction Markets</span>
                                    <div className="flex items-center gap-2">
                                        <Button variant="outline" size="sm" onClick={cleanupStaleMarkets} disabled={cleaningUp}>
                                            <Trash2 className={`h-4 w-4 mr-2 ${cleaningUp ? 'animate-pulse' : ''}`} />
                                            Clean up stale
                                        </Button>
                                        <Button variant="outline" size="sm" onClick={fetchEnabledMarkets}>
                                            <RefreshCw className={`h-4 w-4 mr-2 ${loadingEnabled ? 'animate-spin' : ''}`} />
                                            Refresh
                                        </Button>
                                    </div>
                                </CardTitle>
                                <CardDescription>
                                    Markets currently accepting predictions — races finished 24h+ ago
                                    are auto-removed hourly, or clear them immediately with "Clean up stale"
                                </CardDescription>
                            </CardHeader>
                            <CardContent>
                                {loadingEnabled ? (
                                    <div className="py-12 text-center">
                                        <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                                    </div>
                                ) : enabledMarkets.length === 0 ? (
                                    <div className="py-12 text-center text-muted-foreground">
                                        <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
                                        <p>No markets enabled yet</p>
                                        <p className="text-sm mt-1">Enable predictions for races in the Races tab</p>
                                    </div>
                                ) : (
                                    <div className="space-y-4">
                                        {enabledMarkets.map((market) => (
                                            <div 
                                                key={market.race_id}
                                                className="p-4 rounded-lg border bg-card"
                                            >
                                                <div className="flex items-start justify-between gap-4">
                                                    <div className="flex-1">
                                                        <div className="flex items-center gap-2">
                                                            <span className="font-medium">{market.race_name}</span>
                                                            <Badge variant={market.status === 'open' ? 'default' : 'secondary'}>
                                                                {market.status}
                                                            </Badge>
                                                            {market.is_jump_race && (
                                                                <Badge variant="outline">Jump</Badge>
                                                            )}
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                                            <span className="flex items-center gap-1">
                                                                <MapPin className="h-3 w-3" />
                                                                {market.meeting_name}
                                                            </span>
                                                            <span className="flex items-center gap-1">
                                                                <Clock className="h-3 w-3" />
                                                                {market.race_time ? new Date(market.race_time).toLocaleString('en-GB', {
                                                                    month: 'short',
                                                                    day: 'numeric',
                                                                    hour: '2-digit',
                                                                    minute: '2-digit'
                                                                }) : 'TBC'}
                                                            </span>
                                                        </div>
                                                        <div className="flex items-center gap-4 mt-2">
                                                            <span className="text-sm flex items-center gap-1">
                                                                <BarChart3 className="h-4 w-4 text-primary" />
                                                                {market.markets?.length || 0} markets
                                                            </span>
                                                            <span className="text-sm flex items-center gap-1">
                                                                <Users className="h-4 w-4 text-primary" />
                                                                {market.total_bets || 0} bets
                                                            </span>
                                                            <span className="text-sm flex items-center gap-1">
                                                                <Trophy className="h-4 w-4 text-primary" />
                                                                {market.num_runners} runners
                                                            </span>
                                                        </div>
                                                        {/* Market types */}
                                                        <div className="flex flex-wrap gap-1 mt-2">
                                                            {market.markets?.map((m) => (
                                                                <Badge 
                                                                    key={m.market_type} 
                                                                    variant="secondary"
                                                                    className="text-xs"
                                                                >
                                                                    {MARKET_LABELS[m.market_type]?.icon || '🎯'} {MARKET_LABELS[m.market_type]?.name || m.market_type}
                                                                </Badge>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            onClick={() => viewMarketDetail(market.race_id)}
                                                        >
                                                            <Eye className="h-4 w-4 mr-1" />
                                                            Details
                                                        </Button>
                                                        {market.status === 'open' && (
                                                            <Button
                                                                variant="default"
                                                                size="sm"
                                                                onClick={() => settlePredictions(market.race_id)}
                                                                disabled={settlingRace === market.race_id}
                                                            >
                                                                {settlingRace === market.race_id ? (
                                                                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                                                                ) : (
                                                                    <Check className="h-4 w-4 mr-1" />
                                                                )}
                                                                Settle
                                                            </Button>
                                                        )}
                                                        <Button
                                                            variant="destructive"
                                                            size="sm"
                                                            onClick={() => disablePredictions(market.race_id)}
                                                        >
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Market Detail Dialog */}
            <Dialog open={!!selectedMarketDetail} onOpenChange={() => setSelectedMarketDetail(null)}>
                <DialogContent className="max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Market Details</DialogTitle>
                        <DialogDescription>
                            {selectedMarketDetail?.market?.race_name} - {selectedMarketDetail?.market?.meeting_name}
                        </DialogDescription>
                    </DialogHeader>
                    
                    {loadingDetail ? (
                        <div className="py-8 text-center">
                            <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                        </div>
                    ) : selectedMarketDetail && (
                        <div className="space-y-4">
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                <Card>
                                    <CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold">{selectedMarketDetail.bets?.length || 0}</p>
                                        <p className="text-sm text-muted-foreground">Total Bets</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold">
                                            {selectedMarketDetail.bets?.reduce((sum, b) => sum + (b.stake || 0), 0) || 0}
                                        </p>
                                        <p className="text-sm text-muted-foreground">Total Stake</p>
                                    </CardContent>
                                </Card>
                                <Card>
                                    <CardContent className="p-4 text-center">
                                        <p className="text-2xl font-bold">{selectedMarketDetail.market?.num_runners || 0}</p>
                                        <p className="text-sm text-muted-foreground">Runners</p>
                                    </CardContent>
                                </Card>
                            </div>

                            {/* Market breakdown */}
                            <div className="space-y-3">
                                <h4 className="font-medium">Market Breakdown</h4>
                                {Object.entries(selectedMarketDetail.stats || {}).map(([marketType, stats]) => (
                                    <div key={marketType} className="p-3 rounded-lg bg-muted/50">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-medium flex items-center gap-2">
                                                <span>{MARKET_LABELS[marketType]?.icon || '🎯'}</span>
                                                {MARKET_LABELS[marketType]?.name || marketType}
                                            </span>
                                            <span className="text-sm text-muted-foreground">
                                                {stats.total_bets} bets / {stats.total_stake} pts
                                            </span>
                                        </div>
                                        <div className="grid grid-cols-2 gap-2">
                                            {Object.entries(stats.selections || {}).map(([selId, selStats]) => (
                                                <div 
                                                    key={selId}
                                                    className="text-sm p-2 bg-background rounded"
                                                >
                                                    <span className="font-medium">{selId}</span>
                                                    <span className="text-muted-foreground ml-2">
                                                        {selStats.bets} bets ({selStats.stake} pts)
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>

                            {/* Recent bets */}
                            {selectedMarketDetail.bets?.length > 0 && (
                                <div>
                                    <h4 className="font-medium mb-2">Recent Bets</h4>
                                    <ScrollArea className="h-[200px]">
                                        <div className="space-y-2">
                                            {selectedMarketDetail.bets.slice(0, 20).map((bet) => (
                                                <div 
                                                    key={bet.id}
                                                    className="flex items-center justify-between p-2 rounded bg-muted/30 text-sm"
                                                >
                                                    <div>
                                                        <span className="font-medium">{bet.user_name}</span>
                                                        <span className="text-muted-foreground ml-2">
                                                            {bet.market_name} → {bet.selection_name}
                                                        </span>
                                                    </div>
                                                    <div className="text-right">
                                                        <span>{bet.stake} pts @ {bet.odds}</span>
                                                        <Badge 
                                                            variant={bet.status === 'won' ? 'default' : bet.status === 'lost' ? 'destructive' : 'secondary'}
                                                            className="ml-2"
                                                        >
                                                            {bet.status}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </ScrollArea>
                                </div>
                            )}
                        </div>
                    )}

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setSelectedMarketDetail(null)}>
                            Close
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
